/**
 * Profile personal-details tab.
 */
import React from "react";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { useAsterController } from "context";
import { VText } from "form";
import { COUNTRY_LIST } from "utils/string";

/**
 * Displays the learner's account fields.
 */
const ProfileDetail = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { user, loadedDashboard } = controller;

  return (
    <Box className="p-4 md:p-8">
      <Box>
        <VText className="text-lg font-bold">{t("Personal Information")}</VText>
        <Grid container spacing={2} className="mt-0">
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Full Name")}
            </VText>
            {loadedDashboard ? (
              <VText>
                {user.first_name} {user.last_name}
              </VText>
            ) : (
              <Skeleton className="w-40 h-5" />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Gender")}
            </VText>
            {loadedDashboard ? <VText>{user.gender === "m" ? t("Male") : t("Female")}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Country")}
            </VText>
            {loadedDashboard ? <VText>{COUNTRY_LIST.find((v) => v.code === user.country)?.name || "-"}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
        </Grid>
      </Box>

      <Box className="mt-8">
        <VText className="text-lg font-bold">{t("Contact Information")}</VText>
        <Grid container spacing={2} className="mt-0">
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Phone Number")}
            </VText>
            {loadedDashboard ? <VText>{user.phone_number || "-"}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Email")}
            </VText>
            {loadedDashboard ? <VText>{user.email}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Website")}
            </VText>
            {loadedDashboard ? <VText>{user.website || "-"}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
          <Grid item md={12}>
            <VText className="mb-3" color="secondary" div>
              {t("Address")}
            </VText>
            {loadedDashboard ? <VText>{user.location}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
        </Grid>
      </Box>

      <Box className="mt-8">
        <VText className="text-lg font-bold">{t("Educational Information")}</VText>
        <Grid container spacing={2} className="mt-0">
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("University")}
            </VText>
            {loadedDashboard ? <VText>{user.university || "-"}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Degree")}
            </VText>
            {loadedDashboard ? <VText>{user.degree || "-"}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
        </Grid>
      </Box>

      <Box className="mt-8">
        <VText className="text-lg font-bold">{t("Professional Information")}</VText>
        <Grid container spacing={2} className="mt-0">
          <Grid item xs={12} md={4}>
            <VText className="mb-3" color="secondary" div>
              {t("Role")}
            </VText>
            {loadedDashboard ? <VText>{user.role || "-"}</VText> : <Skeleton className="w-40 h-5" />}
          </Grid>
          <Grid item xs={12} md={8}>
            <VText color="secondary" className="mb-3" div>
              {t("Skills")}
            </VText>
            {loadedDashboard ? (
              <Box className="flex flex-wrap gap-3">
                {!user.skills?.length
                  ? "-"
                  : user.skills.map((v) => (
                      <VText key={v} className="rounded-full bg-gray-100 dark:bg-gray-500 py-1 px-2 cursor-pointer">
                        {v}
                      </VText>
                    ))}
              </Box>
            ) : (
              <Skeleton className="w-40 h-5" />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfileDetail;
